import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogTitle,
} from "../../../../components/ui/Dialog";
import Input from "../../../../components/ui/Input";
import Button from "../../../../components/ui/Button";
import Select from "../../../../components/ui/Select";
import { Checkbox } from "../../../../components/ui/Checkbox";
import AppIcon from "../../../../components/AppIcon";
import * as RadioGroupPrimitive from "@radix-ui/react-radio-group";
import {
  CompanyGrossPayInput, CompanyGrossPayView, AmountCalculationType, GrossPayType, Taxability,
  isBasicSalaryComponent, withBasicSalaryFlag,
} from "../../../../models/payroll";
import { PayrollSalaryBaseMode } from "../../../../models/general";
import { useTranslation } from "react-i18next";
import { clearedBasicWageConsequence } from "./basicWageConsequence";

type Props = {
  open: boolean;
  onClose: () => void;
  onSave: (input: CompanyGrossPayInput) => void;
  input: CompanyGrossPayInput;
  fixedSalaryComponents: CompanyGrossPayView[];
  /**
   * The components that currently carry the basic-salary flag, EXCLUDING the one being edited. The
   * invariant says at most one, so this is normally 0 or 1 long — but a tenant that predates the
   * invariant can hold several, and both directions of the change have to name them. The server
   * MOVES the flag on save, so the consequence belongs on screen BEFORE the save rather than being
   * discovered afterwards.
   */
  otherBasicSalaryComponents?: CompanyGrossPayView[];
  /**
   * CO-163 + CO-249: the tenant's configured salary base modes, so the warnings can state the
   * CONSEQUENCE rather than a generic caution. `BasicOnly` has NO fallback rung — clearing the last
   * flag makes overtime and gratuity pay ZERO (CO-244). `null` = not known yet.
   */
  overtimeBaseMode?: PayrollSalaryBaseMode | null;
  eosBaseMode?: PayrollSalaryBaseMode | null;
};

const GrossPaySettingDialog: React.FC<Props> = ({
  open, onClose, onSave, input, fixedSalaryComponents,
  otherBasicSalaryComponents = [], overtimeBaseMode = null, eosBaseMode = null,
}) => {
  const { t } = useTranslation(['payroll','common']);
  const [localInput, setLocalInput] = useState<CompanyGrossPayInput>(input);
  const [hideFormula, setHideFormula] = useState(false);
  const [hideAmount, setHideAmount] = useState(false);
  /**
   * 🔴 CO-163 — the acknowledgement gate for the DESTRUCTIVE direction.
   * Promoting a component was already loud (red callout + post-save toast). CLEARING the last flag
   * had nothing at all, and it is the direction that can zero statutory pay: on a tenant configured
   * BasicOnly, unticking while tidying a component name silently makes overtime pay nothing. So the
   * save is held behind an explicit tick, exactly as an irreversible action would be.
   */
  const [acknowledged, setAcknowledged] = useState(false);

  useEffect(() => {
    setLocalInput(input);
    setAcknowledged(false);
    switch (input.amountCalculationType) {
      case AmountCalculationType.EmployeeSpecificAmount:
        setHideFormula(true);
        setHideAmount(true);
        break;
      case AmountCalculationType.FormulaBasedAmount:
        setHideFormula(false);
        setHideAmount(true);
        break;
      case AmountCalculationType.FixedAmount:
        setHideFormula(true);
        setHideAmount(false);
        break;
      default:
        break;
    }
  }, [input]);

  // ── Which direction is this save moving the basic-salary flag? ───────────────────────────────
  // `input` is the STORED shape the dialog was seeded from (a CompanyGrossPayView on edit, a blank
  // input on add), so it is the only honest "before". `localInput` is the "after".
  const wasBasicSalary = isBasicSalaryComponent(input);
  const nowBasicSalary = isBasicSalaryComponent(localInput);
  const otherFlagged = otherBasicSalaryComponents;

  /**
   * 🔴 THE ONE FLAG IS THE PER-EMPLOYEE COMPONENT (owner decision, 2026-08-19).
   * The basic salary carries each employee's own amount, so only an `EmployeeSpecificAmount`
   * component can be it — a housing line or a per-company fixed amount never is. The control is
   * therefore offered only on that calculation type… except on a component that is ALREADY flagged,
   * where hiding it would trap a legacy tenant with a flag it cannot clear.
   */
  const isEmployeeSpecific = localInput.amountCalculationType === AmountCalculationType.EmployeeSpecificAmount;
  const showBasicSalaryCheckbox = isEmployeeSpecific || wasBasicSalary;

  /** Ticking / unticking writes ONE explicit value. No derived flip, ever (that was CO-163). */
  const setBasicSalary = (checked: boolean) => setLocalInput((prev) => withBasicSalaryFlag(prev, checked));

  /** false -> true while something else holds the flag: the server will MOVE it on save. */
  const promoting = nowBasicSalary && !wasBasicSalary && otherFlagged.length > 0;
  /** true -> false and nothing else holds it: the company is left with NO statutory basic wage. */
  const clearingLast = wasBasicSalary && !nowBasicSalary && otherFlagged.length === 0;
  /** true -> false but another component still carries it: a change, not a cliff. */
  const clearingWithOthers = wasBasicSalary && !nowBasicSalary && otherFlagged.length > 0;

  /**
   * 🔴 CO-163 — THE CONSEQUENCE, NAMED FROM THE TENANT'S OWN CONFIG (the CO-249 idiom).
   * Shared with the DELETE confirmation through `clearedBasicWageConsequence`: both controls reach
   * the same zero-flag state, so they must make HR the same promise. They did not — the delete
   * dialog never read the base modes at all. See that helper's header.
   */
  const overtimeStrict = overtimeBaseMode === PayrollSalaryBaseMode.BasicOnly;
  const eosStrict = eosBaseMode === PayrollSalaryBaseMode.BasicOnly;
  const clearedConsequence = clearedBasicWageConsequence(t, overtimeBaseMode, eosBaseMode);

  const handleSave = () => {
    // Belt and braces — the button is disabled, but the destructive save must never slip through.
    if (clearingLast && !acknowledged) return;
    onSave(localInput);
  };

  const amountCalcOptions = Object.values(AmountCalculationType).map((x) => ({
    value: x,
    label: t(`payroll:${x.toString()}`),
  }));

  const grossPayTypeOptions = Object.values(GrossPayType).map((x) => ({
    value: x.valueOf().toString(),
    label: t(`payroll:${x.toString()}`),
  }));

  const taxabilityOptions = Object.values(Taxability).map((x) => ({
    value: x.valueOf().toString(),
    label: t(`payroll:${x.toString()}`),
  }));

  const formulaBaseOptions = fixedSalaryComponents.map((x) => ({
    value: x.id.toString(),
    label: x.name,
  }));

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) onClose(); }}>
      <DialogContent size="sm" data-testid="settings-form-dialog">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <div className="rounded-md bg-primary/10 p-1.5">
              <AppIcon name="Settings" size={18} className="text-primary" />
            </div>
            <DialogTitle>
              {input.id ? t('payroll:Edit Gross Pay') : t('payroll:Add New Gross Pay')}
            </DialogTitle>
          </div>
        </DialogHeader>
        <DialogBody>
          <div className="flex flex-col gap-4">
            <Input
              id="name"
              label={t('payroll:Gross Pay Name')}
              type="text"
              value={localInput.name}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLocalInput({ ...localInput, name: e.target.value })}
            />
            <Select
              label={t('payroll:Amount Calculation Type')}
              searchable
              options={amountCalcOptions}
              value={localInput.amountCalculationType}
              onChange={(val: string) => {
                const newInput = { ...localInput };
                switch (val) {
                  case AmountCalculationType.EmployeeSpecificAmount:
                    newInput.amountCalculationType = AmountCalculationType.EmployeeSpecificAmount; setHideFormula(true); setHideAmount(true); break;
                  case AmountCalculationType.FormulaBasedAmount:
                    newInput.amountCalculationType = AmountCalculationType.FormulaBasedAmount; setHideFormula(false); setHideAmount(true); break;
                  case AmountCalculationType.FixedAmount:
                    newInput.amountCalculationType = AmountCalculationType.FixedAmount; setHideFormula(true); setHideAmount(false); break;
                }
                // The basic salary must carry each employee's own amount, so a tick made on
                // "Employee Specific Amount" cannot survive a switch to a formula/fixed amount.
                // A component that arrived ALREADY flagged keeps its flag — clearing it silently
                // while HR edited an unrelated field is exactly the invisible money change the
                // single-flag design exists to prevent; HR must untick it deliberately.
                if (newInput.amountCalculationType !== AmountCalculationType.EmployeeSpecificAmount && !wasBasicSalary) {
                  newInput.isBasicSalary = false;
                }
                setLocalInput(newInput);
              }}
            />
            {/* 🔴 ONE FLAG (owner decision, 2026-08-19). "Base Salary Gross Pay" and "Statutory
                basic wage" were two checkboxes for one real thing: the component that carries each
                employee's own salary amount IS the component every statutory calculation is based
                on. Two boxes let a tenant configure a contradiction (and CO-244 was the result). */}
            <fieldset className="rounded-lg border border-border p-3">
              <legend className="px-1 text-sm font-medium text-foreground">
                {t('payroll:Salary basis')}
              </legend>
              <div className="flex flex-col gap-3">
                {showBasicSalaryCheckbox ? (
                  <Checkbox
                    id="is-basic-salary"
                    checked={nowBasicSalary}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setBasicSalary(e.target.checked)}
                    label={t('payroll:Basic salary component')}
                    description={t("payroll:This component carries each employee's own basic salary, and overtime, end-of-service gratuity, pension (GOSI / GPSSA), CCHI and the WPS salary file are all calculated from it. Only one component per company can be the basic salary.")}
                  />
                ) : (
                  <p className="flex items-start gap-1.5 text-xs text-muted-foreground">
                    <AppIcon name="Info" size={13} className="mt-0.5 shrink-0" />
                    <span className="max-w-[70ch]">
                      {t('payroll:Only a component whose amount is set per employee can be the basic salary. Change the amount calculation to Employee Specific Amount to use this component as the basic salary.')}
                    </span>
                  </p>
                )}
                {/* A legacy row can hold the flag on a formula/fixed component. Say so, rather than
                    letting HR believe the statutory base is a per-employee amount when it is not. */}
                {showBasicSalaryCheckbox && !isEmployeeSpecific && nowBasicSalary && (
                  <p className="flex items-start gap-1.5 text-xs text-muted-foreground">
                    <AppIcon name="Info" size={13} className="mt-0.5 shrink-0" />
                    <span className="max-w-[70ch]">
                      {t("payroll:This component's amount is not set per employee, so the basic salary is the same for everyone it applies to. Normally the basic salary is the component that carries each employee's own amount.")}
                    </span>
                  </p>
                )}
                {/* PROMOTION — the flag is about to MOVE off whatever holds it. */}
                {promoting && (
                  <div
                    role="alert"
                    data-testid="basic-wage-move-warning"
                    className="flex items-start gap-2 rounded-lg border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive"
                  >
                    <AppIcon name="AlertTriangle" size={16} className="mt-0.5 shrink-0" />
                    <p className="max-w-[70ch]">
                      {t('payroll:Saving moves the basic salary off {{name}}. Overtime, end of service, pension and the WPS file change from the next calculation.', { name: otherFlagged.map((c) => c.name).join(', ') })}
                    </p>
                  </div>
                )}

                {/*
                  🔴 THE DESTRUCTIVE DIRECTION — CO-163 / CO-244.
                  Promoting a component was already loud. UNTICKING the last flagged one had nothing
                  at all, yet it is the change that can zero statutory pay: a tenant on BasicOnly has
                  no fallback rung, so unticking while tidying a component name makes overtime pay
                  NOTHING. The consequence is stated from the tenant's OWN configured base modes (the
                  CO-249 idiom), and the save is held behind an explicit acknowledgement.
                */}
                {clearingLast && (
                  <div
                    role="alert"
                    data-testid="basic-wage-clear-warning"
                    className="flex items-start gap-2 rounded-lg border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive"
                  >
                    <AppIcon name="AlertTriangle" size={16} className="mt-0.5 shrink-0" />
                    <div className="max-w-[70ch] space-y-2">
                      <p className="font-medium">
                        {t('payroll:This is the only component flagged as the basic salary. Clearing it leaves the company with none.')}
                      </p>
                      <p>{clearedConsequence}</p>
                      <Checkbox
                        id="ack-clear-basic-wage"
                        checked={acknowledged}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAcknowledged(e.target.checked)}
                        label={t('payroll:I understand this leaves no basic salary component configured.')}
                      />
                    </div>
                  </div>
                )}

                {/* Clearing while another component still carries it: a change, not a cliff. */}
                {clearingWithOthers && (
                  <p className="flex items-start gap-1.5 text-xs text-muted-foreground">
                    <AppIcon name="Info" size={13} className="mt-0.5 shrink-0" />
                    <span className="max-w-[70ch]">
                      {t('payroll:{{names}} will remain the basic salary component, so overtime, end of service and pension are calculated from it instead.', { names: otherFlagged.map((c) => c.name).join(', ') })}
                    </span>
                  </p>
                )}

                {/* Nothing flagged anywhere, and this save does not change that. */}
                {!nowBasicSalary && !wasBasicSalary && otherFlagged.length === 0 && (
                  <p className="flex items-start gap-1.5 text-xs text-muted-foreground">
                    <AppIcon name="Info" size={13} className="mt-0.5 shrink-0" />
                    <span className="max-w-[70ch]">
                      {overtimeStrict || eosStrict
                        ? t('payroll:No component is flagged as the basic salary, and this company has no fallback configured, so gratuity and overtime calculate as ZERO. Flag the component that carries the basic salary.')
                        : t('payroll:No component is flagged as the basic salary yet, so overtime, end of service and pension fall back to other rules.')}
                    </span>
                  </p>
                )}
              </div>
            </fieldset>
            <Checkbox
              checked={localInput.includedInWps}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLocalInput({ ...localInput, includedInWps: e.target.checked })}
              label={t('common:includedInWps')}
            />
            <hr className="border-border" />
            <Select
              label={t('payroll:Gross Pay Type')}
              searchable
              options={grossPayTypeOptions}
              value={localInput.type.toString()}
              onChange={(val: string) => setLocalInput({ ...localInput, type: (val as GrossPayType) || GrossPayType.Allowance })}
            />
            <Select
              label={t('payroll:Taxability')}
              searchable
              options={taxabilityOptions}
              value={localInput.taxability.toString()}
              onChange={(val: string) => setLocalInput({ ...localInput, taxability: (val as Taxability) || Taxability.Exempted })}
            />
            <fieldset>
              <legend className="text-sm font-medium text-foreground mb-2">{t('payroll:Proof Required')}</legend>
              <RadioGroupPrimitive.Root
                value={localInput.proofRequired ? 'YES' : 'NO'}
                onValueChange={(value) => setLocalInput({ ...localInput, proofRequired: value === 'YES' })}
                className="flex items-center gap-4 rtl:space-x-reverse"
              >
                {[
                  { value: 'YES', label: t('common:Yes') },
                  { value: 'NO', label: t('common:No') },
                ].map((opt) => (
                  <label key={opt.value} className="flex items-center gap-2 cursor-pointer">
                    <RadioGroupPrimitive.Item
                      value={opt.value}
                      className="h-4 w-4 rounded-full border border-primary text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 data-[state=checked]:border-primary"
                    >
                      <RadioGroupPrimitive.Indicator className="flex items-center justify-center">
                        <span className="h-2 w-2 rounded-full bg-primary" />
                      </RadioGroupPrimitive.Indicator>
                    </RadioGroupPrimitive.Item>
                    <span className="text-sm text-foreground">{opt.label}</span>
                  </label>
                ))}
              </RadioGroupPrimitive.Root>
            </fieldset>
            {!hideAmount && (
              <Input
                id="amount"
                label={t('payroll:Amount')}
                type="number"
                value={localInput.amount}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLocalInput({ ...localInput, amount: parseFloat(e.target.value) })}
                min={0}
              />
            )}
            {!hideFormula && (
              <div className="flex items-center gap-3 flex-grow">
                <div className="flex-[0_0_40%]">
                  <div className="space-y-2">
                    <label htmlFor="formulaPercentage" className="text-sm font-medium text-foreground">
                      {t('payroll:Formula Percentage')}
                    </label>
                    <div className="relative">
                      <input
                        id="formulaPercentage"
                        type="number"
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 pe-8 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                        value={localInput.formulaPercentage}
                        onChange={(e) => setLocalInput({ ...localInput, formulaPercentage: parseFloat(e.target.value) })}
                      />
                      <span className="absolute end-3 top-1/2 -translate-y-1/2 font-semibold text-primary">%</span>
                    </div>
                  </div>
                </div>
                <div className="flex-[1_1_60%]">
                  <Select
                    label={t('payroll:Gross Pays')}
                    searchable
                    options={formulaBaseOptions}
                    value={localInput.companyGrossPayForFormulaId ? localInput.companyGrossPayForFormulaId.toString() : ''}
                    onChange={(val: string) => setLocalInput({ ...localInput, companyGrossPayForFormulaId: val ? parseInt(val) : 0 })}
                  />
                </div>
              </div>
            )}
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {t('common:Cancel')}
          </Button>
          {/* CO-163: clearing the last statutory flag is destructive and gated on the tick above. */}
          <Button
            onClick={handleSave}
            data-testid="save-btn"
            variant={clearingLast ? 'destructive' : 'default'}
            disabled={clearingLast && !acknowledged}
          >
            {clearingLast ? t('payroll:Save without a basic salary component') : t('common:Submit')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default GrossPaySettingDialog;
